import React from "react";
import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";

const Layout = ({ title, children }) => (
  <div className="min-h-screen bg-surface-base text-white">
    <header className="border-b border-white/5 px-6 py-4">
      <div className="max-w-3xl mx-auto flex justify-between items-center">
        <Link to="/" className="font-display font-black text-xl"><span className="text-gradient-vel7o">Vel7o</span><span className="text-white">.com</span></Link>
        <Link to="/" className="text-sm text-white/60 hover:text-white flex items-center gap-1"><ArrowLeft className="w-3 h-3" /> Inicio</Link>
      </div>
    </header>
    <main className="max-w-3xl mx-auto px-6 py-12">
      <h1 className="font-display font-bold text-4xl mb-8 tracking-tight">{title}</h1>
      <div className="prose prose-invert prose-headings:font-display prose-headings:text-white prose-p:text-white/75 prose-li:text-white/75 prose-strong:text-white max-w-none space-y-4 text-sm leading-relaxed">
        {children}
      </div>
    </main>
    <footer className="border-t border-white/5 px-6 py-8 mt-16 text-center text-xs text-white/40">
      <div className="flex justify-center gap-4 mb-2">
        <Link to="/aviso-legal" className="hover:text-white">Aviso legal</Link>
        <Link to="/privacidad" className="hover:text-white">Privacidad</Link>
        <Link to="/cookies" className="hover:text-white">Cookies</Link>
      </div>
      © {new Date().getFullYear()} Vel7o.com
    </footer>
  </div>
);

const Placeholder = () => (
  <div className="p-4 mb-4 rounded border border-amber-500/30 bg-amber-500/10 text-amber-300 text-xs">
    <strong>Datos del titular pendientes de completar.</strong> Sustituye <code>[RAZÓN SOCIAL]</code>, <code>[CIF/NIF]</code>, <code>[DIRECCIÓN]</code> y <code>[EMAIL]</code> con tus datos reales antes de operar.
  </div>
);

export function AvisoLegal() {
  return (
    <Layout title="Aviso legal">
      <Placeholder />
      <p>En cumplimiento de la Ley 34/2002, de 11 de julio, de Servicios de la Sociedad de la Información y de Comercio Electrónico (LSSI-CE), se facilita la siguiente información:</p>

      <h2>1. Datos del titular</h2>
      <ul>
        <li><strong>Titular:</strong> [RAZÓN SOCIAL]</li>
        <li><strong>CIF/NIF:</strong> [CIF/NIF]</li>
        <li><strong>Domicilio:</strong> [DIRECCIÓN]</li>
        <li><strong>Email de contacto:</strong> [EMAIL]</li>
        <li><strong>Sitio web:</strong> https://vel7o.com</li>
      </ul>

      <h2>2. Objeto</h2>
      <p>El titular pone a disposición de los usuarios el sitio web Vel7o.com (en adelante, "el Sitio"), una plataforma SaaS destinada a la creación y gestión de presupuestos y facturas para autónomos y pymes.</p>

      <h2>3. Condiciones de uso</h2>
      <p>El acceso al Sitio es gratuito. El uso de las funcionalidades requiere registro previo. El usuario se compromete a usar el Sitio conforme a la ley, la moral y el orden público.</p>

      <h2>4. Propiedad intelectual</h2>
      <p>Todos los contenidos del Sitio (textos, imágenes, código, diseños) son propiedad del titular o de sus licenciantes. Queda prohibida su reproducción sin autorización expresa.</p>

      <h2>5. Responsabilidad</h2>
      <p>El titular no se hace responsable de los daños y perjuicios derivados del uso del Sitio. La información generada por la IA tiene carácter orientativo; el usuario es responsable de verificarla.</p>

      <h2>6. Legislación aplicable</h2>
      <p>Estas condiciones se rigen por la legislación española. Para cualquier controversia, las partes se someten a los Juzgados y Tribunales del domicilio del titular.</p>
    </Layout>
  );
}

export function Privacidad() {
  return (
    <Layout title="Política de privacidad">
      <Placeholder />
      <p>De conformidad con el Reglamento (UE) 2016/679 (RGPD) y la LOPDGDD 3/2018, le informamos sobre el tratamiento de sus datos personales:</p>

      <h2>1. Responsable del tratamiento</h2>
      <ul>
        <li><strong>Identidad:</strong> [RAZÓN SOCIAL] — [CIF/NIF]</li>
        <li><strong>Domicilio:</strong> [DIRECCIÓN]</li>
        <li><strong>Contacto:</strong> [EMAIL]</li>
      </ul>

      <h2>2. Finalidades</h2>
      <ul>
        <li>Gestión de la cuenta de usuario (email, contraseña, datos de empresa)</li>
        <li>Prestación del servicio (almacenamiento de tus presupuestos, facturas, clientes, logos)</li>
        <li>Gestión del cobro de la suscripción Pro</li>
        <li>Atención al cliente y envío de comunicaciones operativas</li>
      </ul>

      <h2>3. Base jurídica</h2>
      <p>El tratamiento se basa en la ejecución del contrato (registro y uso del servicio), el consentimiento (cookies analíticas) y obligaciones legales (facturación).</p>

      <h2>4. Plazo de conservación</h2>
      <p>Los datos se conservan mientras tengas cuenta activa. Datos contables se conservan al menos 6 años (art. 30 Código de Comercio).</p>

      <h2>5. Destinatarios</h2>
      <p>Tus datos pueden compartirse con:</p>
      <ul>
        <li><strong>Stripe</strong> (pasarela de pago) — datos de pago. Política: stripe.com/privacy</li>
        <li><strong>OpenAI</strong> (asistente IA) — únicamente el texto que escribes en el prompt</li>
        <li><strong>Resend</strong> (envío de emails) — solo email del destinatario del PDF</li>
        <li><strong>Emergent</strong> (hosting y almacenamiento) — datos cifrados en tránsito y reposo</li>
      </ul>

      <h2>6. Derechos</h2>
      <p>Tienes derecho a acceder, rectificar, suprimir, oponerte, limitar y portar tus datos. Puedes ejercerlos enviando un email a [EMAIL]. También puedes reclamar ante la Agencia Española de Protección de Datos (www.aepd.es).</p>

      <h2>7. Seguridad</h2>
      <p>Aplicamos medidas técnicas y organizativas para proteger tus datos: cifrado de contraseñas (bcrypt), HTTPS, autenticación JWT y backups regulares.</p>
    </Layout>
  );
}

export function Cookies() {
  return (
    <Layout title="Política de cookies">
      <Placeholder />
      <h2>1. ¿Qué son las cookies?</h2>
      <p>Las cookies son pequeños archivos que se almacenan en tu dispositivo cuando visitas una web. Sirven para guardar información como tu sesión iniciada.</p>

      <h2>2. Cookies utilizadas en Vel7o</h2>
      <p>Vel7o.com utiliza únicamente <strong>almacenamiento técnico esencial</strong>:</p>
      <ul>
        <li><strong>vel7o_token</strong> (localStorage) — almacena tu sesión iniciada. Sin esto no puedes acceder a tu cuenta. Caduca a los 30 días.</li>
        <li><strong>vel7o_pwa_dismissed</strong> (localStorage) — recuerda si has cerrado el aviso de instalación de la app.</li>
      </ul>
      <p><strong>No usamos cookies de analítica, publicidad ni terceros</strong>. No necesitas aceptar nada.</p>

      <h2>3. Cookies de terceros</h2>
      <p>En el proceso de pago, Stripe puede establecer sus propias cookies necesarias para la operativa de pago seguro. Consulta su política en stripe.com/cookie-settings.</p>

      <h2>4. Cómo deshabilitar cookies</h2>
      <p>Puedes deshabilitar el almacenamiento desde la configuración de tu navegador, pero perderás tu sesión iniciada y no podrás usar Vel7o.</p>

      <h2>5. Contacto</h2>
      <p>Para cualquier duda sobre cookies: [EMAIL]</p>
    </Layout>
  );
}
